/**
 * The three kinds of team member an operator picks between, and what each one
 * implies.
 *
 * This mirrors `app/services/user_archetypes.py`. The backend is authoritative
 * for what is actually written; this copy exists so the dialog can show the
 * consequences of a choice without a round-trip per click.
 */

import type { UserArchetype } from '../../../services/api';

export interface ArchetypeOption {
  value: UserArchetype;
  label: string;
  /** Clinics decide where they can be scheduled, so clinicians must pick some. */
  needsClinics: boolean;
  /** Titles decide which visits they can take. */
  needsTitles: boolean;
  /** Whether a Roger App account is offered at all. */
  offersAppAccount: boolean;
}

export const ARCHETYPE_OPTIONS: ArchetypeOption[] = [
  {
    value: 'FIELD_CLINICIAN',
    label: 'Field clinician',
    needsClinics: true,
    needsTitles: true,
    offersAppAccount: true,
  },
  {
    value: 'CLINICAL_SUPERVISOR',
    label: 'Clinical supervisor',
    needsClinics: true,
    needsTitles: true,
    offersAppAccount: true,
  },
  {
    value: 'BACK_OFFICE',
    label: 'Back office',
    needsClinics: false,
    needsTitles: false,
    offersAppAccount: false,
  },
];

/**
 * Roles an archetype always grants, shown but never editable.
 *
 * Mirrors the blocking tier in `app/services/user_archetypes.py`. These decide
 * whether somebody can be scheduled, so they stay in code -- making them
 * configurable would recreate the bug this work exists to remove, with the blast
 * radius of every future user. Listed here only so a configuration screen can
 * show what is fixed alongside what is not.
 */
export const ARCHETYPE_GUARANTEED_ROLES: Record<
  UserArchetype,
  readonly string[]
> = {
  FIELD_CLINICIAN: ['Member', 'Clinician'],
  CLINICAL_SUPERVISOR: ['Member', 'Clinician', 'Case Manager'],
  BACK_OFFICE: ['Member'],
};

export function archetypeOption(value: UserArchetype): ArchetypeOption {
  const found = ARCHETYPE_OPTIONS.find(option => option.value === value);
  if (!found) {
    throw new Error(`No archetype option for ${value}`);
  }
  return found;
}
